import {
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { GradientCard, SkeletonCard } from "./GradientCard";
import { Button } from "./ui/button";
import { gradients } from "@/data/gradients";
import { encodeGradient, parseGradientCSS } from "@/lib/gradient-url";
import { filterGradientsByColors } from "@/lib/wizard";
import { generateCSV, downloadCSV } from "@/lib/csv-export";
import type {
  GradientPreset,
  GradientCategory,
  WizardColor,
  GradientTypeFilter,
  UIPreviewMode,
  ColorFormat,
} from "@/types";

interface GradientGalleryProps {
  category: GradientCategory | "All" | "Favorites";
  searchQuery: string;
  colors: WizardColor[];
  tags: string[];
  gradientType: GradientTypeFilter;
  previewMode: UIPreviewMode;
  colorFormat: ColorFormat;
  selectedAnimationId: string | null;
  animationSpeed: number;
  favorites: string[]; // Encoded gradient definitions
  onSelectGradient: (gradient: GradientPreset) => void;
  onToggleFavorite: (encodedGradient: string) => void;
  isFavorite: (encodedGradient: string) => boolean;
  onClearFilters: () => void;
}

// Pre-compute encoded gradients once at module load for performance
const encodedGradientMap = new Map<string, string>();
gradients.forEach((g) => {
  const def = parseGradientCSS(g.gradient);
  if (def) {
    encodedGradientMap.set(g.id, encodeGradient(def));
  }
});

function getEncodedGradient(preset: GradientPreset): string | null {
  return encodedGradientMap.get(preset.id) ?? null;
}

// Hook to get responsive column count
function useColumnCount() {
  const [columns, setColumns] = useState(() => {
    if (typeof window === "undefined") return 4;
    const width = window.innerWidth;
    if (width < 640) return 1;
    if (width < 1024) return 2;
    if (width < 1280) return 3;
    return 4;
  });

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 640) setColumns(1);
      else if (width < 1024) setColumns(2);
      else if (width < 1280) setColumns(3);
      else setColumns(4);
    };

    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  return columns;
}

// Threshold for when to use virtualization (only for large lists)
const VIRTUALIZATION_THRESHOLD = 100;

// Download bar component
function DownloadBar({
  gradients,
  isFavorites,
}: {
  gradients: GradientPreset[];
  isFavorites: boolean;
}) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownload = useCallback(async () => {
    setIsGenerating(true);

    // Small delay to show the animation (CSV generation is instant)
    await new Promise((resolve) => setTimeout(resolve, 300));

    const csv = generateCSV(gradients);
    const filename = isFavorites
      ? "goodgradients-favorites.csv"
      : "goodgradients-export.csv";
    downloadCSV(csv, filename);

    setIsGenerating(false);
  }, [gradients, isFavorites]);

  if (gradients.length === 0) return null;

  return (
    <div className="mt-8 mb-4 flex justify-center">
      <button
        onClick={handleDownload}
        disabled={isGenerating}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-800/50 hover:bg-neutral-800 border border-neutral-700 text-xs text-neutral-400 hover:text-neutral-300 transition-colors disabled:cursor-not-allowed"
      >
        {isGenerating ? (
          <span className="animate-download-pulse">Generating...</span>
        ) : (
          <>
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download CSV
          </>
        )}
      </button>
    </div>
  );
}

export function GradientGallery({
  category,
  searchQuery,
  colors,
  tags,
  gradientType,
  previewMode,
  selectedAnimationId,
  animationSpeed,
  favorites,
  onSelectGradient,
  onToggleFavorite,
  isFavorite,
  onClearFilters,
}: GradientGalleryProps) {
  const listRef = useRef<HTMLDivElement>(null);
  /*
    The virtualizer needs the list's distance from the top of the document to
    convert window scroll into list coordinates. Reading listRef during render
    gave it 0 on the first pass, because the node does not exist yet, so every
    virtual row was positioned against the wrong origin until some later
    render happened to correct it. Measuring in a layout effect and holding it
    in state means the value is real before it is ever used, and it is
    remeasured when the window resizes, which is when it actually changes.
  */
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const measure = () => setScrollMargin(listRef.current?.offsetTop ?? 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const columns = useColumnCount();
  const [isHydrated, setIsHydrated] = useState(false);

  // Brief skeleton display on initial mount for smooth perceived loading
  useEffect(() => {
    // Use requestAnimationFrame to ensure we show skeleton for at least one frame
    const frame = requestAnimationFrame(() => {
      setIsHydrated(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // colorFormat was removed from GradientCard (hex codes no longer shown on cards)
  // It's still passed here for API compatibility but not forwarded to cards
  const filteredGradients = useMemo(() => {
    let result: GradientPreset[] = gradients;

    // Apply category filter first
    if (category === "Favorites") {
      result = result.filter((g) => {
        const encoded = getEncodedGradient(g);
        return encoded && favorites.includes(encoded);
      });
    } else if (category !== "All") {
      result = result.filter((g) => g.category === category);
    }

    // Apply colors filter
    if (colors.length > 0) {
      result = filterGradientsByColors(result, colors);
    }

    // Apply tags filter (gradient must have ALL selected tags - AND logic)
    if (tags.length > 0) {
      result = result.filter((g) => tags.every((tag) => g.tags.includes(tag)));
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      result = result.filter(
        (g) =>
          g.name.toLowerCase().includes(searchLower) ||
          g.description.toLowerCase().includes(searchLower) ||
          g.tags.some((t) => t.toLowerCase().includes(searchLower)) ||
          g.colors.some((c) => c.toLowerCase().includes(searchLower)),
      );
    }

    // Sort by name alphabetically
    result = [...result].sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }, [category, searchQuery, colors, tags, favorites]);

  // Calculate row count for virtualization
  const rowCount = Math.ceil(filteredGradients.length / columns);
  const useVirtualization = filteredGradients.length > VIRTUALIZATION_THRESHOLD;

  // Window virtualizer for large lists
  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: useCallback(() => 240, []), // Row height estimate: landscape card (~16:9 swatch + 48px content + 24px gap)
    overscan: 5,
    scrollMargin,
    enabled: useVirtualization,
  });

  // Show skeleton grid during initial hydration
  if (!isHydrated) {
    const skeletonCount = columns * 3; // 3 rows of skeletons
    return (
      <section aria-label="Loading gradients">
        <header className="mb-6">
          <div className="h-8 w-64 rounded loading-shimmer mb-2" />
          <div className="h-4 w-32 rounded loading-shimmer" />
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </section>
    );
  }

  if (filteredGradients.length === 0) {
    const hasFilters =
      colors.length > 0 ||
      tags.length > 0 ||
      searchQuery.length > 0 ||
      category !== "All";

    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-neutral-800 flex items-center justify-center">
          <span className="text-2xl">🎨</span>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">
          {category === "Favorites" ? "No favorites yet" : "No gradients found"}
        </h3>
        <p className="text-neutral-400 max-w-sm mb-4">
          {category === "Favorites"
            ? "Start adding gradients to your favorites by clicking the heart icon."
            : "Try adjusting your filters or search query."}
        </p>
        {hasFilters && category !== "Favorites" && (
          <Button variant="outline" onClick={onClearFilters}>
            Reset filters
          </Button>
        )}
      </div>
    );
  }

  // For small lists, render without virtualization
  if (!useVirtualization) {
    return (
      <section aria-label="Gradient gallery">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredGradients.map((gradient) => {
            const encoded = getEncodedGradient(gradient);
            return (
              <GradientCard
                key={gradient.id}
                gradient={gradient}
                gradientType={gradientType}
                previewMode={previewMode}
                selectedAnimationId={selectedAnimationId}
                animationSpeed={animationSpeed}
                isFavorite={encoded ? isFavorite(encoded) : false}
                onToggleFavorite={() => encoded && onToggleFavorite(encoded)}
                onSelect={onSelectGradient}
              />
            );
          })}
        </div>
        <DownloadBar
          gradients={filteredGradients}
          isFavorites={category === "Favorites"}
        />
      </section>
    );
  }

  // Virtualized rendering for large lists
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <section aria-label="Gradient gallery">
      <div ref={listRef}>
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {virtualItems.map((virtualRow) => {
            const startIndex = virtualRow.index * columns;
            const rowGradients = filteredGradients.slice(
              startIndex,
              startIndex + columns,
            );

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-6">
                  {rowGradients.map((gradient) => {
                    const encoded = getEncodedGradient(gradient);
                    return (
                      <GradientCard
                        key={gradient.id}
                        gradient={gradient}
                        gradientType={gradientType}
                        previewMode={previewMode}
                        selectedAnimationId={selectedAnimationId}
                        animationSpeed={animationSpeed}
                        isFavorite={encoded ? isFavorite(encoded) : false}
                        onToggleFavorite={() =>
                          encoded && onToggleFavorite(encoded)
                        }
                        onSelect={onSelectGradient}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <DownloadBar
        gradients={filteredGradients}
        isFavorites={category === "Favorites"}
      />
    </section>
  );
}
